"use client";

import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Drawer,
  Divider,
  Switch,
  FormControlLabel,
  Paper,
  Chip,
  Link,
  Tooltip,
  Alert,
} from "@mui/material";
import { Close, Download, ReportGmailerrorred } from "@mui/icons-material";
import { COMPLEXITY, COMPLEXITY_BGC, COMPLEXITY_COLORS, COMPLEXITY_CONTRACTION } from "../../../constants";
import { API_BASE_URL } from "../../../../services/api/endpoints";
import { downloadBase64AsZip, downloadContent } from "../../../../utils/file-utils";
import axios from "axios";
import { useAccountId } from "../../../../hooks/useAccountId";

interface ApiDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  api: any;
  migrationId: string;
  // xAccountId: string;
}

// Definimos una interfaz para los metadatos de los chips
interface MetadataChip {
  label: string;
  tooltipTitle: string;
  tooltipDescription: string;
}

export function getComplexityName(complexity: number): string {
  return COMPLEXITY[complexity as keyof typeof COMPLEXITY] || "Unknown Complexity";
}

export function getComplexityLabel(complexity: number): string {
  return COMPLEXITY_CONTRACTION[complexity as keyof typeof COMPLEXITY] || "Unknown Complexity";
}

export function getComplexityColor(complexity: number): string {
  return COMPLEXITY_COLORS[complexity as keyof typeof COMPLEXITY] || "Unknown Complexity";
}

export function getComplexityBGC(complexity: number): string {
  return COMPLEXITY_BGC[complexity as keyof typeof COMPLEXITY] || "Unknown Complexity";
}

export function ApiDetailsModal({ isOpen, onClose, api, migrationId }: ApiDetailsModalProps) {
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState(false);

  const xAccountId = useAccountId();

  // Definimos los metadatos para cada chip
  const metadataChips: MetadataChip[] = [
    {
      label: `${api.resourceAmount} Resources`,
      tooltipTitle: "Resources",
      tooltipDescription: "Amount of resources (Operations)",
    },
    {
      label: `${api.stepsAmount} Steps`,
      tooltipTitle: "Step",
      tooltipDescription: "Number of steps in the API flow",
    },
    {
      label: `${api.orchestrationAmount} Orchestrations`,
      tooltipTitle: "Orchestrations",
      tooltipDescription: "Number of orchestration components",
    },
    {
      label: `${api.transformationActions} Transformations`,
      tooltipTitle: "Transformations",
      tooltipDescription: "Number of data transformations",
    },
    {
      label: `${api.migratableManualStepsAmount} Migratable Steps`,
      tooltipTitle: "Migratable Steps",
      tooltipDescription: "Number of steps that can be migrated automatically",
    },
  ];

  const handleDownloadDetailFile = async (migrationId: string, sourceFileName: string) => {
    const requestHeaders = {
      "X-ACCOUNT-ID": xAccountId,
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    try {
      let url = `${API_BASE_URL}/api/v1/migrations/${migrationId}/files/${sourceFileName}?file_type=SOURCE`;
      setShowAlert(true);
      const response = await axios
        .get(url, {
          headers: requestHeaders,
        })
        .then((res: any) => {
          return res.data;
        });

      if ((await response.status) === 200 && (await response.content!)) {
        if (sourceFileName!.includes(".zip")) {
          await downloadBase64AsZip(response.content, sourceFileName!);
        } else {
          await downloadContent(response.content, sourceFileName!);
        }
        const successDownloadMsg = `File ${sourceFileName} downloaded successfully`;
        setDownloadSuccess(successDownloadMsg);
      }
    } catch (error) {
      const errorMsg = `Error downloading file: ${error instanceof Error ? error.message : String(error)}`;
      setDownloadError(errorMsg);
      console.error(errorMsg);
      setShowAlert(true);
    }
  };

  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showAlert]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "700px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      {/* Succes de descarga */}
      {downloadSuccess && showAlert && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {downloadSuccess}
        </Alert>
      )}
      {/* Error de descarga */}
      {downloadError && showAlert && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error de descarga: {downloadError}
        </Alert>
      )}
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6">
          View <strong>{api.apiName}</strong> details
        </Typography>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <Close />
        </IconButton>
      </Box>
      {api && api.complexity ? (
        <Box>
          {/* Info message */}
          <Box sx={{ padding: "16px 24px" }}>
            <Typography variant="body2" color="text.secondary">
              You will be redirected to the Assessment landing page to confirm the status of the task.
            </Typography>
          </Box>
          {/* Content */}
          <Box sx={{ padding: "0 24px", overflow: "auto", flex: 1 }}>
            {/* File Information */}
            <Paper variant="outlined" sx={{ mb: 3, overflow: "hidden" }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  File Information
                </Typography>
              </Box>

              {/* <Divider /> */}

              <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle2">Download Original File</Typography>
                <Link
                  href="#"
                  underline="none"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "#2196f3",
                    fontSize: "0.875rem",
                  }}
                  onClick={() => handleDownloadDetailFile(migrationId, api.sourceFileName)}
                >
                  {api.sourceFileName}
                  <Download sx={{ ml: 0.5, fontSize: 16 }} />
                </Link>
              </Box>

              <Divider />

              <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="subtitle2">Exclude api from migration from table</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Optional Skip this file in future migrations.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={api.excluded}
                      // onChange={(e) => setExcludeFromMigration(e.target.checked)}
                      size="small"
                    />
                  }
                  disabled
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Exclude
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ ml: 0, mr: 0 }}
                />
              </Box>
            </Paper>

            {/* Complexity */}
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Complexity
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={getComplexityLabel(api.complexity)}
                    size="small"
                    sx={{
                      bgcolor: getComplexityBGC(api.complexity),
                      color: getComplexityColor(api.complexity),
                      fontSize: "0.75rem",
                      height: "20px",
                    }}
                  />
                  {/* <Typography variant="body2">{getComplexityText(api.complexity)}</Typography> */}
                </Box>
              </Box>
            </Paper>

            {/* Metadata Section */}
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Metadata Section
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, position: "relative" }}>
                  {metadataChips.map((chip, index) => (
                    <Tooltip
                      key={index}
                      title={
                        <Box>
                          <Typography variant="subtitle2">{chip.tooltipTitle}</Typography>
                          <Typography variant="body2">{chip.tooltipDescription}</Typography>
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <Chip
                        label={chip.label}
                        size="small"
                        sx={{
                          bgcolor: "#424242",
                          color: "white",
                          fontSize: "0.75rem",
                          height: "24px",
                          cursor: "pointer", // Añadir cursor pointer para indicar que es interactivo
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </Paper>

            {/* Migrations Details */}
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Migrations Details
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  To migrate the API proxy from IBM API Connect to Apigee, follow these steps:
                </Typography>
                <Box component="ol" sx={{ pl: 2, mt: 1 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Instructive:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                      <Box component="li">
                        <p
                          style={{
                            overflowWrap: "break-word",
                            whiteSpace: "pre-wrap",
                            maxWidth: "100%",
                            wordBreak: "break-word",
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {api.instructive}
                          </Typography>
                        </p>
                      </Box>
                    </Box>
                  </Box>

                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Describe Transformation Actions:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                      <Box component="li">
                        {/* <pre> */}
                        <Typography variant="body2" color="text.secondary">
                          {api.describeTransformationActions}
                        </Typography>
                        {/* </pre> */}
                      </Box>
                    </Box>
                  </Box>

                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Describe Steps:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                      <Box component="li">
                        <Typography variant="body2" color="text.secondary">
                          {api.describeStep}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Describe Orchestration Amount:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                      <Box component="li">
                        <Typography variant="body2" color="text.secondary">
                          {api.describeOrchestrationAmount}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      Describe Migratable Manual Steps Amount
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                      <Box component="li">
                        <Typography variant="body2" color="text.secondary">
                          {api.describeMigratableManualStepsAmount}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {api.apiName && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        Files to Create:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                        <Box component="li">
                          <Typography variant="body2" color="text.secondary">
                            {api.apiName}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "error.light",
            overflowWrap: "break-word",
            wordWrap: "break-word",
            wordBreak: "break-word",
            hyphens: "auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", maxWidth: "calc(100% - 40px)" }}>
            <ReportGmailerrorred sx={{ color: "error.dark", mr: 1, flexShrink: 0 }} />
            <Typography
              variant="h6"
              sx={{
                color: "error.dark",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              THE ASSESSMENT COULD NOT BE PERFORMED
            </Typography>
          </Box>
        </Box>
      )}

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          Close detail
        </Button>
      </Box>
    </Drawer>
  );
}
